import { createContext, useContext, useState, useEffect } from "react";
import { getConfig, getAuth, getTOTPAuthenticator } from "../library/allauth";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(undefined);
  const [config, setConfig] = useState(undefined);
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    async function init() {
      try {
        const [configData, authData] = await Promise.all([
          getConfig(),
          getAuth(),
        ]);
        setConfig({ data: configData, loading: false });
        if (authData.status === 200 && authData.meta?.is_authenticated) {
          setUser(authData.data.user);
          try {
            const mfaRes = await getTOTPAuthenticator();
            setMfaRequired(mfaRes.status !== 200);
          } catch {
            setMfaRequired(true);
          }
        }
      } catch (error) {
        console.error("Failed to load config or auth:", error);
        setConfig();
      } finally {
        setLoading(false);
      }
    }
    init();

    // Listen for auth changes
    const handleAuthChange = async (event) => {
      const msg = event.detail;
      if (msg.status === 200 && msg.meta?.is_authenticated) {
        setUser(msg.data?.user);
        try {
          const mfaRes = await getTOTPAuthenticator();
          setMfaRequired(mfaRes.status !== 200);
        } catch {
          setMfaRequired(true);
        }
      } else if (msg.status === 410) {
        setUser(null);
        setMfaRequired(false);
      } else if (
        msg.status === 401 &&
        msg.meta?.is_authenticated === false &&
        !msg.meta?.session_token
      ) {
        setUser(null);
        setMfaRequired(false);
      }
    };

    document.addEventListener("allauth.auth.change", handleAuthChange);
    return () =>
      document.removeEventListener("allauth.auth.change", handleAuthChange);
  }, []);

  return (
    <AuthContext.Provider
      value={{ config, user, setUser, loading, mfaRequired }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useConfig must be used within AuthProvider");
  }
  return context.config;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
